import { useState, useEffect, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Breadcrumbs from "examples/BreadcrumbsDashboard";
import { useNavigate } from "react-router-dom";
import { useClerk, useUser } from "@clerk/clerk-react";
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";
import patchnotes from "../../../patchnotes.json";

const API_URL = process.env.REACT_APP_API_URL;
const API_KEY = process.env.REACT_APP_API_KEY;

function DashboardNavbar({ absolute, light, isMini }) {
  const { signOut } = useClerk();
  const { user } = useUser();
  const navigate = useNavigate();
  const hasCheckedRef = useRef(false);

  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openModal, setOpenModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [readNotes, setReadNotes] = useState([]);
  const [showFirstTimeTitle, setShowFirstTimeTitle] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  const route = useLocation().pathname.split("/").slice(1);
  const latestPatchnote = patchnotes[0];

  const unreadCount = readNotes.length === 0
    ? 1
    : patchnotes.filter(note => !readNotes.includes(note.id)).length;

  const openPatchnoteModal = (note, firstTime = false) => {
    setSelectedNote(note);
    setShowFirstTimeTitle(firstTime);
    setOpenModal(true);
    if (!readNotes.includes(note.id)) {
      const updated = [...readNotes, note.id];
      setReadNotes(updated);
      sessionStorage.setItem("readPatchnotes", JSON.stringify(updated));

      if (user?.id) {
        fetch(`${API_URL}/user-patchnotes`, {
          method: "POST",
          headers: {
            "x-api-key": API_KEY,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ user_id: user.id, patchnote_id: note.id })
        });
      }
    }
  };

  useEffect(() => {
    if (!user || hasCheckedRef.current) return;

    const alreadyChecked = sessionStorage.getItem("checkedPatchnotes");
    const readLocal = sessionStorage.getItem("readPatchnotes");
    if (readLocal) {
      try {
        setReadNotes(JSON.parse(readLocal));
      } catch (e) {
        console.error("Fehler beim Parsen von readPatchnotes:", e);
      }
    }

    hasCheckedRef.current = true;

    if (!alreadyChecked) {
      fetch(`${API_URL}/user-patchnotes/${user.id}`, {
        headers: {
          "x-api-key": API_KEY,
          "Content-Type": "application/json",
        },
      })
        .then(res => res.json())
        .then(data => {
          const read = data.map(p => p.patchnote_id);
          setReadNotes(read);
          sessionStorage.setItem("readPatchnotes", JSON.stringify(read));
          sessionStorage.setItem("checkedPatchnotes", "true");

          if (!read.includes(latestPatchnote.id)) {
            openPatchnoteModal(latestPatchnote, true);
          }
        })
        .catch(console.error);
    }
  }, [user]);

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }
    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  const handleLogout = async () => {
    setUserMenuAnchor(null);
    await signOut();
    navigate("/login");
  };

  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;
      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }
      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
          <Switch checked={miniSidenav} onChange={handleMiniSidenav} />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })} display="flex" alignItems="center" gap={0.5}>
            {/* Mobile menu toggle */}
            <Tooltip title="Navigation umschalten" arrow>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
            </Tooltip>

            {/* Settings */}
            <Tooltip title="Einstellungen" arrow>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={handleConfiguratorOpen}
              >
                <Icon sx={iconsStyle}>settings</Icon>
              </IconButton>
            </Tooltip>

            {/* Patchnotes */}
            <Tooltip title="Patchnotes" arrow>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={() => openPatchnoteModal(latestPatchnote)}
              >
                <Badge color="error" variant="dot" invisible={unreadCount === 0}>
                  <Icon sx={iconsStyle}>article</Icon>
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Divider */}
            <MDBox
              sx={{
                width: "1px",
                height: "24px",
                backgroundColor: darkMode ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.12)",
                mx: 1,
              }}
            />

            {/* User menu */}
            <MDBox
              display="flex"
              alignItems="center"
              sx={{ cursor: "pointer", gap: 1, borderRadius: 2, px: 1, py: 0.5,
                "&:hover": { backgroundColor: darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)" }
              }}
              onClick={(e) => setUserMenuAnchor(e.currentTarget)}
            >
              <Avatar
                src={user?.imageUrl}
                sx={{ width: 32, height: 32 }}
                alt={user?.firstName || "User"}
              />
              <MDTypography
                variant="button"
                fontWeight="medium"
                color={light || darkMode ? "white" : "dark"}
                sx={{ display: { xs: "none", md: "block" } }}
              >
                {user?.firstName || "Benutzer"}
              </MDTypography>
              <Icon sx={{ ...iconsStyle, fontSize: "18px !important" }}>expand_more</Icon>
            </MDBox>

            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={() => setUserMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: { mt: 1, minWidth: 180, borderRadius: 2, boxShadow: 3 },
              }}
            >
              <MenuItem
                onClick={() => { setUserMenuAnchor(null); navigate("/profile"); }}
              >
                <ListItemIcon><Icon fontSize="small">person</Icon></ListItemIcon>
                <ListItemText>Profil</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => { setUserMenuAnchor(null); navigate("/dashboard"); }}
              >
                <ListItemIcon><Icon fontSize="small">dashboard</Icon></ListItemIcon>
                <ListItemText>Dashboard</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ color: "error.main" }}>
                <ListItemIcon><Icon fontSize="small" color="error">logout</Icon></ListItemIcon>
                <ListItemText>Abmelden</ListItemText>
              </MenuItem>
            </Menu>
          </MDBox>
        )}

        {/* Patchnotes Dialog */}
        <Dialog
          open={openModal}
          onClose={() => setOpenModal(false)}
          maxWidth="sm"
          fullWidth
          disableEnforceFocus
        >
          <DialogTitle sx={{ textAlign: "center" }}>{selectedNote?.title}</DialogTitle>
          <DialogContent>
            {showFirstTimeTitle && (
              <Typography variant="body2" fontWeight="bold" align="center" gutterBottom>
                Neue Version veröffentlicht
              </Typography>
            )}
            <Typography variant="caption" display="block" align="center" gutterBottom>
              {selectedNote?.date}
            </Typography>
            {selectedNote?.sections.map((section, i) => (
              <div key={i} style={{ marginBottom: 24 }}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  {section.title}
                </Typography>
                {section.items.map((item, j) => (
                  <div key={j} style={{ marginLeft: 16, marginBottom: 12 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {item.title}
                    </Typography>
                    <Typography variant="body2">{item.description}</Typography>
                  </div>
                ))}
              </div>
            ))}
          </DialogContent>
        </Dialog>
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
